import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchJobDetails } from '../actions/confirm-job-action/job-details.action';
import { CustomerInfoJobDetails, CustomerInfoState, MileInfo, UserInfo } from '../types/store.types';

const mileInfoDummyData = {
  Option: '',
  Bedrooms: '',
  'Move Type': '',
  size: '',
  address1: '',
  address2: '',
  sq: '',
  weight: '',
  floors: '',
  longCarry: '',
  date: '--/--/--',
  city: '',
  state: '',
  zip_code: '',
  country: '',
};

const initialState: CustomerInfoState = {
  loading: false,
  error: null,
  prevUserInfo: {},
  prevCrewNotes: undefined,
  jobDetails: {},
  toAddress: {},
  prevMilesToDestinatonInfo: {},
  _id: '',
  fromOffice: 0,
  originToDestination: 0,
  destinationToOffice: 0,
  moveType: {},
  distance: {},
  slot: '',
  userInfo: {
    id: 'N/A',
    fullName: 'N/A',
    status: 'N/A',
    phone: 'N/A',
    email: 'N/A',
    startTime: 'N/A',
    endTime: 'N/A',
    fromZone: '',
    toZone: '',
    startDate: '',
    endDate: '',
  },
  prevMileInfo: [
    {
      Option: 'stairs',
      Bedrooms: '3B',
      'Move Type': 'Apartment',
      size: '10*5',
      address: '4324 McClellan Road,#105 Dallas,TX',
      sq: '1,200 sq ft',
      weight: '1,300 Ibs',
      floors: 'Floors:3',
      longCarry: 'L.C:50 ft',
      date: '4/12/24',
      city: '',
      state: '',
      zip: '',
      country: '',
    },
  ],
  mileInfo: [
    {
      Option: 'stairs',
      Bedrooms: '3B',
      'Move Type': 'Apartment',
      size: '10*5',
      address1: '',
      address2: '',
      sq: '0',
      weight: '0',
      floors: '0',
      longCarry: '0',
      date: '4/12/24',
      city: '',
      state: '',
      zip_code: '',
      country: '',
    },
  ],
  milesToDestinatonInfo: {
    Option: 'stairs',
    Bedrooms: '3B',
    'Move Type': 'Apartment',
    size: '10*5',
    address1: '',
    address2: '',
    sq: '0',
    weight: '0',
    floors: '0',
    longCarry: '0',
    date: '4/12/24',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  },
  crewDetails: {},
  crewSize: 0,
  trucks: 0,
  crewNotesInfo: { notes: '' },
};

export const customerInfoSlice = createSlice({
  name: 'customerInfo',
  initialState,
  reducers: {
    updateUserInfo: (state, action: PayloadAction<{ type: keyof UserInfo; value: any }>) => {
      state.userInfo = {
        ...state.userInfo,
        [action.payload.type]: action.payload.value,
      };
    },
    updateMileInfo: (state, action: PayloadAction<{ type: keyof MileInfo; value: any; index: number }>) => {
      const { type, value, index } = action.payload;
      const newMileInfo = [...state.mileInfo];
      newMileInfo[index][type] = value;
      state.mileInfo = newMileInfo;
    },
    addStop: (state, _action) => {
      state.mileInfo.push({
        ...mileInfoDummyData,
        date: state.mileInfo[0].date,
      });
    },
    updateMileToDestination: (state, action: PayloadAction<{ type: keyof MileInfo; value: any }>) => {
      const { type, value } = action.payload;
      const newMileToDestinationInfo = { ...state.milesToDestinatonInfo };
      newMileToDestinationInfo[type] = value;
      state.milesToDestinatonInfo = newMileToDestinationInfo;
    },
    updateCrewNotes: (state, action: PayloadAction<{ isUpdate: boolean; value: string }>) => {
      if (action.payload.isUpdate) {
        state.crewNotesInfo.notes = action.payload.value;
        if (!state.prevCrewNotes) {
          state.prevCrewNotes = { notes: '' };
        }
        state.prevCrewNotes.notes = action.payload.value;
      } else {
        state.crewNotesInfo.notes = action.payload.value;
      }
    },
    removeAddStop: (state, action: PayloadAction<number>) => {
      state.mileInfo = state.mileInfo.filter((_item, index) => index !== action.payload);
    },
    updateSlots: (state, action: PayloadAction<string>) => {
      state.slot = action.payload;
    },
    fetchCustomerInfoDetails: (state, action: PayloadAction<CustomerInfoJobDetails>) => {
      const {
        _id,
        customer,
        startTime,
        endTime,
        status,
        crew_notes: CrewNotesDetail,
        crew,
        crew_size: crewSize,
        from_address: fromAddress,
        additional_stops: additionalStops,
        to_address: toAddress,
        from_info: fromInfo,
        to_info: toInfo,
        office_to_origin: fromOffice,
        origin_to_destination: originToDestination,
        destination_to_office: destinationToOffice,
        move_type: moveType,
        distance,
        move_date: moveDate,
        slot,
      } = action.payload;
      state.loading = false;
      state.mileInfo = state.mileInfo.slice(0, 1);
      state.userInfo.fullName = `${customer?.first_name} ${customer?.last_name}`;
      state.userInfo.email = customer?.email;
      state.userInfo.phone = customer?.phone;
      state.userInfo.id = customer?._id || '';
      state.userInfo.startTime = convertToSimpleTime(startTime).split(' ')[0];
      state.userInfo.endTime = convertToSimpleTime(endTime).split(' ')[0];
      state.userInfo.fromZone = convertToSimpleTime(startTime).split(' ')[1];
      state.userInfo.toZone = convertToSimpleTime(endTime).split(' ')[1];
      state.userInfo.status = status;
      state.slot = slot;
      state.crewNotesInfo.notes = CrewNotesDetail;
      state.crewDetails = crew;
      state.crewSize = crewSize;
      state.trucks = crew?.trucks?.length;
      state.fromOffice = fromOffice;
      state.originToDestination = originToDestination;
      state.destinationToOffice = destinationToOffice;
      state.moveType = moveType;
      state.distance = distance;
      state.mileInfo[0].address1 = fromAddress?.address1 ? fromAddress?.address1 : '';
      state.mileInfo[0].address2 = fromAddress?.address2 ? fromAddress?.address2 : '';
      state.mileInfo[0].city = fromAddress?.city ? fromAddress?.city : '';
      state.mileInfo[0].country = fromAddress?.country ? fromAddress?.country : '';
      state.mileInfo[0].state = fromAddress?.state ? fromAddress?.state : '';
      state.mileInfo[0].zip_code = fromAddress?.zip_code ? fromAddress?.zip_code : '';
      state.mileInfo[0]['Move Type'] = fromInfo?.move_size?.type;
      state.mileInfo[0].Bedrooms = fromInfo?.move_size?.value;

      state.mileInfo[0].Option = fromInfo.elevator ? 'elevator' : fromInfo?.stairs ? 'stairs' : '';
      state.mileInfo[0].longCarry = fromInfo?.lc;
      state.mileInfo[0].sq = fromInfo?.sqft;
      state.mileInfo[0].floors = fromInfo?.floors;
      state.mileInfo[0].weight = fromInfo?.cube;

      state.toAddress = toAddress;
      state.milesToDestinatonInfo = {
        ...state.milesToDestinatonInfo,
        Option: toInfo.elevator ? 'elevator' : toInfo?.stairs ? 'stairs' : '',
        longCarry: toInfo.lc,
        sq: toInfo.sqft,
        floors: toInfo.floors,
        weight: toInfo.cube,
        'Move Type': toInfo?.move_size?.type,
        Bedrooms: toInfo?.move_size?.value,
        ...toAddress,
      };
      state.mileInfo[0].date = formatDateString(startTime || moveDate, 'mile');
      state.milesToDestinatonInfo.date = formatDateString(endTime || moveDate, 'mile');

      state.prevMilesToDestinatonInfo = state.milesToDestinatonInfo;
      state.jobDetails = action.payload;
      state.prevUserInfo = state.userInfo;
      state.prevCrewNotes = state.crewNotesInfo;
      state.error = null;
      state._id = _id;
      state.userInfo.endDate = formatDateString(endTime || moveDate);
      state.userInfo.startDate = formatDateString(startTime || moveDate);
      additionalStops?.map((item) =>
        state.mileInfo.push({
          Option: item?.info?.stairs ? 'stairs' : item?.info?.elevator ? 'elevator' : '',
          Bedrooms: item?.info?.move_size?.value,
          'Move Type': item?.info?.move_size?.type,
          size: item?.info?.move_size?.value,
          address1: item?.address1 ? item?.address1 : '',
          address2: item?.address2 ? item?.address2 : '',
          sq: item?.info?.sqft,
          weight: item?.info?.cube,
          floors: item?.info?.floors,
          longCarry: item?.info?.lc,
          date: state?.mileInfo[0]?.date,
          city: item?.city ? item?.city : '',
          state: item?.state ? item?.state : '',
          zip_code: item?.zip_code ? item?.zip_code : '',
        }),
      );
      state.prevMileInfo = state.mileInfo;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchJobDetails.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(fetchJobDetails.fulfilled, (state, { payload }: { payload: CustomerInfoJobDetails }) => {
      const {
        _id,
        customer,
        startTime,
        endTime,
        status,
        crew_notes: CrewNotesDetail,
        crew,
        crew_size: crewSize,
        from_address: fromAddress,
        additional_stops: additionalStops,
        to_address: toAddress,
        from_info: fromInfo,
        to_info: toInfo,
        office_to_origin: fromOffice,
        origin_to_destination: originToDestination,
        destination_to_office: destinationToOffice,
        move_type: moveType,
        distance,
        slot,
        move_date: moveDate,
      } = payload;
      state.loading = false;
      state.mileInfo = state.mileInfo.slice(0, 1);
      state.userInfo.fullName = `${customer?.first_name} ${customer?.last_name}`;
      state.userInfo.email = customer?.email;
      state.userInfo.phone = customer?.phone;
      state.userInfo.id = customer?._id;
      state.userInfo.startTime = convertToSimpleTime(startTime || moveDate).split(' ')[0];
      state.userInfo.endTime = convertToSimpleTime(endTime || moveDate).split(' ')[0];
      state.userInfo.fromZone = convertToSimpleTime(startTime || moveDate).split(' ')[1];
      state.userInfo.toZone = convertToSimpleTime(endTime || moveDate).split(' ')[1];
      state.userInfo.status = status;
      // TODO:: state.userInfo.slot = slot;
      state.slot = slot;
      state.crewNotesInfo.notes = CrewNotesDetail;
      state.crewDetails = crew;
      state.crewSize = crewSize;
      state.trucks = crew?.trucks?.length;
      state.fromOffice = fromOffice;
      state.originToDestination = originToDestination;
      state.destinationToOffice = destinationToOffice;
      state.moveType = moveType;
      state.distance = distance;
      state.mileInfo[0].address1 = fromAddress?.address1 || '';
      state.mileInfo[0].address2 = fromAddress?.address2 || '';
      state.mileInfo[0].city = fromAddress?.city || '';
      state.mileInfo[0].country = fromAddress?.country || '';
      state.mileInfo[0].state = fromAddress?.state || '';
      state.mileInfo[0].zip_code = fromAddress?.zip_code || '';

      state.mileInfo[0].Option = fromInfo.elevator ? 'elevator' : fromInfo?.stairs ? 'stairs' : '';
      state.mileInfo[0].longCarry = fromInfo?.lc;
      state.mileInfo[0].sq = fromInfo?.sqft;
      state.mileInfo[0].floors = fromInfo?.floors;
      state.mileInfo[0].weight = fromInfo?.cube;

      state.toAddress = toAddress;
      state.milesToDestinatonInfo = {
        ...state.milesToDestinatonInfo,
        Option: toInfo.elevator ? 'elevator' : toInfo?.stairs ? 'stairs' : '',
        longCarry: toInfo.lc,
        sq: toInfo.sqft,
        floors: toInfo.floors,
        weight: toInfo.cube,
        ...toAddress,
      };
      state.mileInfo[0].date = formatDateString(startTime || moveDate, 'mile');
      state.milesToDestinatonInfo.date = formatDateString(endTime || moveDate, 'mile');

      state.prevMilesToDestinatonInfo = state.milesToDestinatonInfo;
      state.jobDetails = payload;
      state.prevUserInfo = state.userInfo;
      state.prevCrewNotes = state.crewNotesInfo;
      state.error = null;
      state._id = _id;
      state.userInfo.endDate = formatDateString(endTime || moveDate);
      state.userInfo.startDate = formatDateString(startTime || moveDate);
      additionalStops?.map((item) =>
        state.mileInfo.push({
          Option: item?.info?.stairs ? 'stairs' : item?.info?.elevator ? 'elevator' : '',
          Bedrooms: item?.info?.move_size?.value,
          'Move Type': item?.info?.move_size?.type,
          size: item?.info?.move_size?.value,
          address1: item?.address1 || '',
          address2: item?.address2 || '',
          sq: item?.info?.sqft,
          weight: item?.info?.cube,
          floors: item?.info?.floors,
          longCarry: item?.info?.lc,
          date: state?.mileInfo[0]?.date,
          city: item?.city,
          state: item?.state,
          zip_code: item?.zip_code,
        }),
      );
      state.prevMileInfo = state.mileInfo;
    });

    builder.addCase(fetchJobDetails.rejected, (state, { payload }) => {
      state.loading = false;
      state.jobDetails = {};
      // @ts-expect-error Not sure, what to put here.
      state.error = payload;
    });
  },
});
export const customerInfoActions = customerInfoSlice.actions;
export const customerInfoReducer = customerInfoSlice.reducer;

const formatDateString = (dateString: string, type?: 'mile' | 'user'): string => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return type ? `${day}/${month}/${String(year).slice(2)}` : `${year}-${month}-${day}`;
};

const convertToSimpleTime = (dateStr: string): string => {
  if (dateStr === null || dateStr === '' || dateStr?.slice(11, 19) === '00:00:00') {
    return '';
  }
  const date = new Date(dateStr);
  const localHours = date.getUTCHours();
  const localMinutes = date.getUTCMinutes();
  const hours = localHours % 12 || 12;
  const minutes = localMinutes.toString().padStart(2, '0');
  const period = localHours >= 12 ? 'PM' : 'AM';
  return `${hours}:${minutes} ${period}`;
};
